import React, {FC, ReactNode} from 'react';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {collectEnumValues} from '../../../../../libs/shared/core/src/models/api/types/enumTypes';
import {getHasSearchHighlightAlgorithmSelector} from '../../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {DropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemContent} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {SimpleDropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdown';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverCoordinator} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppSelector} from '../../../../helpers/appReduxHelpers';
import {getCellsSearchHighlightAlgorithm} from '../../../../store/panels/cells/search/cellsPanelSearchSelectors';
import {HighlightAlgorithmsEnum} from '../../../../store/panels/cells/search/cellsPanelSearchState';
import {StyledSearchBarRightButton} from '../../../common/searchBar/searchBar';

/*
 * Constants.
 */

const highlightAlgorithmsEnumValues = collectEnumValues(HighlightAlgorithmsEnum);

const highlightAlgorithmsLabels: Record<HighlightAlgorithmsEnum, ReactNode> = {
  [HighlightAlgorithmsEnum.DEFAULT]: <Trans id="highlight-algo-default">Default</Trans>,
};

/*
 * Props.
 */

interface InboxesSearchHighlightsAlgorithmProps {
  hasFocus: boolean;
  onHighlightAlgorithmSelect: (arg: HighlightAlgorithmsEnum) => void;
}

/*
 * Component.
 */

export const InboxesSearchHighlightsAlgorithm: FC<InboxesSearchHighlightsAlgorithmProps> = (props) => {
  const {hasFocus, onHighlightAlgorithmSelect} = props;

  const theme = useUserTheme();

  const highlightAlgorithm = useAppSelector(getCellsSearchHighlightAlgorithm);
  const selectedHighlightAlgorithm = highlightAlgorithm ?? HighlightAlgorithmsEnum.DEFAULT;

  const hasHighlightAlgorithms = useAppSelector(getHasSearchHighlightAlgorithmSelector);

  if (!hasHighlightAlgorithms) {
    return null;
  }

  return (
    <PopoverCoordinator
      renderButton={(isOpen) => (
        <StyledSearchBarRightButton>
          <Icon
            name="preferencesSmall"
            color={hasFocus ? theme.greys.shade70 : theme.greys.white}
            hoverColor={hasFocus ? theme.greys.shade80 : theme.greys.shade30}
          />
        </StyledSearchBarRightButton>
      )}
      renderModal={(popoverProps) => (
        <SimpleDropdown {...popoverProps}>
          {highlightAlgorithmsEnumValues.map((algo) => (
            <DropdownItem
              key={algo}
              isChecked={selectedHighlightAlgorithm === algo}
              onSelect={() => onHighlightAlgorithmSelect(algo)}
              shouldReserveRightIcon
            >
              <DropdownItemContent isChecked={selectedHighlightAlgorithm === algo}>
                {highlightAlgorithmsLabels[algo]}
              </DropdownItemContent>
            </DropdownItem>
          ))}
        </SimpleDropdown>
      )}
    />
  );
};
